export const OCCUPANCY_COLORS = {
  occupied: { fill: 'rgba(239, 68, 68, 0.55)', stroke: '#dc2626', label: 'Occupied', chip: 'red' },
  vacant: { fill: 'rgba(34, 197, 94, 0.45)', stroke: '#16a34a', label: 'Vacant', chip: 'green' },
  reserved: { fill: 'rgba(234, 179, 8, 0.5)', stroke: '#ca8a04', label: 'Reserved', chip: 'yellow' },
  under_maintenance: { fill: 'rgba(107, 114, 128, 0.5)', stroke: '#4b5563', label: 'Under Maintenance', chip: 'gray' },
};

export const WALL_STROKE_WIDTH = 6;

export const ELEMENT_DEFAULT_STYLE = {
  office: { fill: 'rgba(59, 130, 246, 0.35)', stroke: '#2563eb', strokeWidth: 3 },
  coworking: { fill: 'rgba(168, 85, 247, 0.3)', stroke: '#7c3aed', strokeWidth: 3 },
  seat: { fill: 'rgba(34, 197, 94, 0.4)', stroke: '#16a34a', strokeWidth: 2.5 },
  meeting_room: { fill: 'rgba(14, 165, 233, 0.35)', stroke: '#0284c8', strokeWidth: 3 },
  wall: { fill: 'transparent', stroke: '#1f2937', strokeWidth: WALL_STROKE_WIDTH },
  door: { fill: 'rgba(251, 146, 60, 0.2)', stroke: '#c2410c', strokeWidth: 3 },
  stairs: { fill: 'rgba(107, 114, 128, 0.35)', stroke: '#4b5563', strokeWidth: 3 },
  area: { fill: 'rgba(20, 184, 166, 0.25)', stroke: '#0d9488', strokeWidth: 3 },
};

/** Real spaces — must be linked to an existing entity (placed from the unplaced list). */
export const LINKABLE_TYPES = new Set(['office', 'coworking', 'seat', 'meeting_room']);

/** Structural / decorative extras — layout geometry only, no entity models. */
export const STRUCTURAL_TYPES = new Set(['wall', 'door', 'stairs', 'area']);

/** Types that use Konva Transformer (walls use endpoint handles instead). */
export const TRANSFORMABLE_TYPES = new Set(['office', 'coworking', 'seat', 'meeting_room', 'door', 'stairs', 'area']);

export const ENTITY_PLACE_DEFAULTS = {
  office: { shapeType: 'rect', w: 120, h: 90 },
  coworking: { shapeType: 'rect', w: 160, h: 120 },
  seat: { shapeType: 'ellipse', w: 32, h: 32 },
  meeting_room: { shapeType: 'rect', w: 100, h: 80 },
};

/** Labeled areas that exist only on the layout (no backend models). */
export const AREA_PRESETS = [
  { value: 'washroom', label: 'Washroom' },
  { value: 'lounge', label: 'Lounge' },
  { value: 'reception', label: 'Reception' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'cafeteria', label: 'Cafeteria' },
  { value: 'storage', label: 'Storage' },
  { value: 'prayer_room', label: 'Prayer room' },
  { value: 'other', label: 'Other area' },
];

export const ELEMENT_TYPES = [
  { value: 'office', label: 'Private Office' },
  { value: 'coworking', label: 'Coworking Area' },
  { value: 'seat', label: 'Seat' },
  { value: 'meeting_room', label: 'Meeting Room' },
  { value: 'wall', label: 'Wall' },
  { value: 'door', label: 'Door' },
  { value: 'stairs', label: 'Stairs' },
  { value: 'area', label: 'Labeled area' },
];

export const FILTERABLE_TYPES = [
  { value: 'office', label: 'Private Office' },
  { value: 'coworking', label: 'Coworking Area' },
  { value: 'seat', label: 'Seat' },
  { value: 'meeting_room', label: 'Meeting Room' },
];

export function getShapeFill(shape, { colorByOccupancy = false } = {}) {
  if (colorByOccupancy && shape.occupancyStatus && OCCUPANCY_COLORS[shape.occupancyStatus]) {
    return OCCUPANCY_COLORS[shape.occupancyStatus].fill;
  }
  return shape.style?.fill || ELEMENT_DEFAULT_STYLE[shape.elementType]?.fill || 'rgba(156,163,175,0.4)';
}

export function getShapeStroke(shape, { colorByOccupancy = false, highlighted = false } = {}) {
  if (highlighted) return '#f97316';
  if (colorByOccupancy && shape.occupancyStatus && OCCUPANCY_COLORS[shape.occupancyStatus]) {
    return OCCUPANCY_COLORS[shape.occupancyStatus].stroke;
  }
  return shape.style?.stroke || ELEMENT_DEFAULT_STYLE[shape.elementType]?.stroke || '#6b7280';
}

export function createShapeId() {
  return `shape_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function snapToGrid(value, gridSize = 20) {
  return Math.round(value / gridSize) * gridSize;
}

export function getEntityLabel(shape) {
  if (shape.text) return shape.text;
  if (shape.occupancy?.entity?.code) return shape.occupancy.entity.code;
  if (shape.occupancy?.entity?.seatCode) return shape.occupancy.entity.seatCode;
  if (shape.elementType === 'stairs') return 'Stairs';
  if (shape.elementType === 'door') return '';
  if (shape.elementType === 'wall') return '';
  if (shape.elementType === 'area') {
    const preset = AREA_PRESETS.find((p) => p.value === shape.facilityKind);
    return preset?.label || 'Area';
  }
  return shape.elementType?.replace(/_/g, ' ') || '';
}

export function canEditFloorPlan(role) {
  return role === 'admin' || role === 'manager' || role === 'super_admin';
}

export function sortShapesByZ(shapes = []) {
  return [...shapes].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
}

export function getWallPoints(shape) {
  if (shape.points?.length >= 4) return shape.points;
  return [0, 0, shape.width || 120, 0];
}

export function wallLength(shape) {
  const [x1, y1, x2, y2] = getWallPoints(shape);
  return Math.round(Math.hypot(x2 - x1, y2 - y1));
}
